/** DnD behavior for one player rendered inside a tactical slot. */
import { useDraggable } from "@dnd-kit/core";
import React, { memo, useCallback, useContext, useMemo } from "react";

import type { Pressable } from "@/components/atoms/Pressable";
import {
  pitchPlayerDragId,
  type WatchlistPitchPlayerDragData,
} from "@/features/scouting/components/molecules/WatchlistPitchDndContext";
import { PitchPlayerNavigationContext } from "@/features/scouting/contexts/PitchPlayerNavigationContext";
import type {
  ScoutingGetWatchlistPitchQuery,
  TacticalSlot,
} from "@/generated/graphql";

import PitchPositionPlayerLineView from "./PitchPositionPlayerLineView";

/** One authorized watchlist member. */
export type PitchPositionPlayer = NonNullable<
  ScoutingGetWatchlistPitchQuery["getWatchlistPitch"]
>["members"]["edges"][number]["node"];

/**
 * Where a member's removal stands (SE-8353): busy while it is sent, Retry
 * after a refusal, re-offered after a revision conflict. Undefined = idle.
 */
export type PitchMemberRemoveState = "removing" | "failed" | "conflict";

/** Props for one player line within a Pitch position node. */
export interface PitchPositionPlayerLineProps {
  readonly player: PitchPositionPlayer;
  readonly isPrimary: boolean;
  readonly slot: TacticalSlot;
  readonly canAssign: boolean;
  readonly assignmentSlots: readonly TacticalSlot[];
  readonly onAssign: (playerId: string, slot: TacticalSlot | null) => void;
  /**
   * Removes this player from the scenario (SE-8353). Absent on the watchlist
   * Pitch, where the line renders exactly as before.
   */
  readonly onRemove?: (playerId: string) => void;
  /** Where this player's removal stands; idle when omitted. */
  readonly removeState?: PitchMemberRemoveState;
}

/**
 * Registers the field-specific drag identity and prepares pure view bindings.
 * @param props - Player, slot, permission, and assignment options.
 */
const PitchPositionPlayerLineContainer = (
  props: PitchPositionPlayerLineProps
) => {
  const dragData = useMemo<WatchlistPitchPlayerDragData>(
    () => ({ kind: "pitch-player", playerId: props.player.playerId }),
    [props.player.playerId]
  );
  const { attributes, listeners, setNodeRef } = useDraggable({
    id: pitchPlayerDragId(props.player.playerId),
    data: dragData,
    disabled: !props.canAssign,
  });
  const dragBindings = useMemo(
    () =>
      ({
        ...attributes,
        ...listeners,
      }) as Partial<React.ComponentProps<typeof Pressable>>,
    [attributes, listeners]
  );
  const { onRemove, player } = props;
  const handleRemove = useCallback(
    () => onRemove?.(player.playerId),
    [onRemove, player.playerId]
  );
  // SE-8605: the name opens the player page wherever the Pitch provides one.
  const openPlayer = useContext(PitchPlayerNavigationContext);
  const handleOpenPlayer = useCallback(
    () => openPlayer?.(player.playerId, player.displayName),
    [openPlayer, player.displayName, player.playerId]
  );
  return (
    <PitchPositionPlayerLineView
      {...props}
      nodeRef={setNodeRef}
      dragBindings={dragBindings}
      onRemove={onRemove ? handleRemove : undefined}
      onOpenPlayer={openPlayer ? handleOpenPlayer : undefined}
    />
  );
};

PitchPositionPlayerLineContainer.displayName =
  "PitchPositionPlayerLineContainer";
export default memo(PitchPositionPlayerLineContainer);
